import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, RouterModule } from '@angular/router';
import { APP_BASE_HREF, CommonModule, LocationStrategy, PlatformLocation } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CarouselModule as ngxCarouselModule, } from 'ngx-bootstrap/carousel';
import { provideLottieOptions } from 'ngx-lottie';

import {
  I18nService,
  InitData,
  InterceptorService,
  PreferenceService,
  ProjectService,
  StudioFactory,
  StudioService,
  TypeFactoryService,
  UserService,
  FileService
} from '@wms/services';
import {
  FileWorkspaceComponent,
  WmsWorkspaceManagerService,
} from '@wms/workspace';
import {
  HeaderComponent,
  LeftpanelComponent,
  WmsDialogComponent,
  WmsDialogHeaderComponent,
  WmsDialogBodyComponent,
  WmsDialogFooterComponent,
  WmsTreeComponent,
  WmsTreeNodeComponent,
  WmsSpinnerComponent,
  WmsTypeaheadComponent,
} from '@wms/components';
import { PrefabManager } from '@wms/page-workspace';

import { APP_ROUTES } from './app.routes';
import { AppComponent } from './app.component';
import { EventManagerService } from './event-manager';
import { LiveWidgetSettingsService } from '@wms/services';
import { WmCommandProvider } from './wmCommand';
import { ToastrModule } from 'ngx-toastr';
import { PageLayoutsService } from './workspace/page-workspace/services/pageLayoutsService';
import { WsManager } from './workspace/page-workspace/WsManager';
import { AppManagerService, AppRef, AppResourceManagerService } from './design-container';
import { environment } from "@wms/app";
import { WmsHelpPanelComponent } from './components/wms-help-panel/wms-help-panel.component';
import { WmsSessionTimeoutComponent } from '@wms/app/components/wms-session-timeout/wms-session-timeout.component';
import { VersionManagerPipe } from '@wms/core';
import { RestImportUiComponent } from './workspace/api-workspace/rest-import-ui/rest-import-ui.component';
import { FormsModule } from '@angular/forms';
import { CodeInspectComponent } from '@wms/bottom-left-menu';
import { PathLocationStrategyWithoutHistoryState } from './pathLocationStrategyWithoutHistory';
import {IssuesComponent} from "@wms/app/bottom-left-menu/components/issues/issues.component";
import { CopilotComponent } from "@wms/copilot";
import { StudioAIWorkspaceComponent } from './workspace/studio-ai-workspace/studio-ai-workspace.component';
import { WmsSsoSessionTimeoutComponent } from "@wms/app/components/wms-sso-session-timeout/wms-sso-session-timeout.component";

export function playerFactory() {
  return import(/* webpackChunkName: 'lottie-web' */ 'lottie-web');
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    RouterModule,
    CommonModule,
    HeaderComponent,
    CodeInspectComponent,
    LeftpanelComponent,
    WmsDialogComponent,
    WmsDialogHeaderComponent,
    WmsDialogBodyComponent,
    WmsDialogFooterComponent,
    WmsTreeComponent,
    FileWorkspaceComponent,
    WmsTreeNodeComponent,
    WmsSpinnerComponent,
    WmsTypeaheadComponent,
    ToastrModule.forRoot(),
    WmsHelpPanelComponent,
    ngxCarouselModule.forRoot(),
    WmsSessionTimeoutComponent,
    WmsSsoSessionTimeoutComponent,
    VersionManagerPipe,
    RestImportUiComponent,
    FormsModule,
    IssuesComponent,
    CopilotComponent,
    StudioAIWorkspaceComponent
  ],
  providers: [
    provideLottieOptions({
      player: playerFactory
    }),
    provideRouter(APP_ROUTES),
    provideAnimations(),
    I18nService,
    EventManagerService,
    LiveWidgetSettingsService,
    WmCommandProvider,
    LiveWidgetSettingsService,
    PageLayoutsService,
    WsManager,
    AppManagerService,
    AppRef,
    { provide: 'ENV_SERVICES_BASE_PATH', useValue: environment.SERVICES_BASE_PATH },
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation]
    },
    {
      provide: 'APP_BUILD_META_INFO',
      useValue: {
        BASE_URL: environment.BASE_URL,
        CDN_BASE_URL: environment.CDN_BASE_URL,
        BUILD_NUMBER: environment.BUILD_NUMBER
      }
    },
    {
      provide: APP_INITIALIZER,
      useFactory: InitData,
      deps: [
        StudioService,
        UserService,
        ProjectService,
        PreferenceService,
        TypeFactoryService,
        StudioFactory,
        I18nService,
        FileService
      ],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterceptorService,
      multi: true
    },
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategyWithoutHistoryState
    },
    WmsWorkspaceManagerService,
    PrefabManager,
    AppResourceManagerService,
    VersionManagerPipe,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {
  constructor() {
    let wmProjectLoaderElement: HTMLElement | null = document.getElementById('wmProjectLoader');

    if (wmProjectLoaderElement) {
      wmProjectLoaderElement.classList.remove("hidden")
    }
  }
}
